import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";

const SERVICES = [
  {
    icon: "ri-film-line",
    title: "影视内容浏览",
    desc: "汇集电影、电视剧、综艺等多类型影视资源，提供清晰的分类与详情信息，帮助用户快速了解作品。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选与搜索",
    desc: "支持按题材、地区、年份、热度等维度筛选，并提供全站关键词搜索，高效定位感兴趣的内容。",
  },
  {
    icon: "ri-newspaper-line",
    title: "影视资讯阅读",
    desc: "提供行业新闻、片场花絮与深度评论，帮助用户及时掌握影视行业的最新动态与趋势。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "本地收藏管理",
    desc: "用户可将感兴趣的作品收藏至本地浏览器，方便随时回访，收藏状态仅保存在个人设备上。",
  },
];

const CONTACTS = [
  { icon: "ri-mail-line", label: "内容合作", value: "contact@www17c.example" },
  { icon: "ri-customer-service-2-line", label: "用户反馈", value: "service@www17c.example" },
  { icon: "ri-weibo-fill", label: "官方微博", value: "@WWW17C影视平台" },
  { icon: "ri-wechat-fill", label: "微信公众号", value: "WWW-17C" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - WWW.17C影视平台",
    description:
      "了解 WWW.17C 影视平台的发展目标、服务范围与联系方式。我们致力于为影视爱好者提供清晰的影视内容浏览与资讯获取体验。",
    keywords: "关于WWW.17C,影视平台介绍,影视内容浏览,影视资讯,WWW.17C",
  });

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      {/* 头部 */}
      <div className="mt-4 rounded-lg bg-primary-950 px-6 py-10 md:px-12 md:py-14">
        <h1 className="font-heading text-3xl font-extrabold text-background-50 md:text-4xl">
          关于 WWW.<span className="text-accent-500">17C</span>
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300 md:text-base">
          WWW.17C 是一个面向所有影视爱好者的综合影视内容平台。我们希望用专业而简洁的方式，
          把丰富的影视资源与及时的行业资讯整理到一处，让每一次浏览都轻松、愉快、有所收获。
        </p>
      </div>

      {/* 发展目标 */}
      <section className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
            我们的发展目标
          </h2>
          <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700">
            <p>
              WWW.17C 希望成为影视爱好者了解作品、发现好内容的入口。我们不追求花哨的功能堆砌，
              而是专注于把「找到想看的」这件事做到足够简单——清晰的分类、合理的筛选、可靠的信息组织。
            </p>
            <p>
              未来，我们将持续扩充影视内容库，覆盖更多地区与类型；同时不断优化页面结构与移动端体验，
              让用户在电脑和手机上都能顺畅浏览。我们也计划引入更多维度的作品信息与关联推荐，
              帮助用户从一个作品出发，发现更多相似或有共鸣的内容。
            </p>
            <p>
              需要说明的是，WWW.17C 目前定位为影视内容的浏览与信息获取平台，
              不提供在线播放、下载、用户登录或在线支付等功能，页面中的收藏与分享均为前端演示。
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="font-heading text-base font-bold text-foreground-950">平台数据一览</h2>
          <div className="mt-5 space-y-4">
            {[
              { label: "收录影视作品", value: "24+" },
              { label: "覆盖内容类型", value: "3 大类" },
              { label: "影视资讯文章", value: "12+" },
              { label: "支持筛选维度", value: "4 维度" },
            ].map((s) => (
              <div key={s.label} className="flex items-center justify-between border-b border-background-200 pb-3 last:border-0">
                <span className="text-sm text-foreground-600">{s.label}</span>
                <span className="font-heading text-lg font-bold text-accent-600">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 服务范围 */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
          我们的服务范围
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s) => (
            <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${s.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mt-12">
        <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
          联系我们
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CONTACTS.map((c) => (
            <div key={c.label} className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${c.icon} text-2xl`}></i>
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{c.label}</p>
                <p className="truncate text-sm font-medium text-foreground-900">{c.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 免责声明 / 版权声明 */}
      <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-shield-check-line text-accent-500"></i>
            免责声明
          </h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
            <li>本站所有影视作品的封面、简介等信息均为示意内容，仅用于页面功能与展示效果的演示。</li>
            <li>本站不提供任何影视作品的在线播放、下载或存储服务，也不对作品的版权归属作出主张。</li>
            <li>若相关权利人对本站展示的示意信息存在异议，可通过下方联系方式与我们取得联系，我们将及时处理。</li>
          </ul>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-copyright-line text-accent-500"></i>
            版权声明
          </h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
            <li>本站的页面设计、信息结构与文字描述（除注明来源的内容外）版权归 WWW.17C 所有。</li>
            <li>资讯栏目中标注来源的文章，其观点与内容不代表本站立场，仅供信息参考。</li>
            <li>未经许可，任何单位或个人不得以任何形式复制、转载或用于商业用途。</li>
            <li>© 2026 WWW.17C 影视平台 · 保留所有权利。</li>
          </ul>
        </div>
      </section>
    </div>
  );
}